/**
 * Lazy loader for the Google Maps JS API. The tenant SPA fetches the API
 * key via `/api/google-maps-config` (which the host proxies, gated by
 * tenant settings), then injects the script tag exactly once per session.
 *
 * Idempotent: callers may invoke from multiple components concurrently —
 * the in-flight Promise is shared so we never inject two `<script>` tags.
 *
 * Why not preload in index.html? Two reasons:
 *   1) Some tenants don't have GMaps configured — paying the script
 *      download on every page load is wasteful.
 *   2) The key is tenant-scoped and only known after auth (shell token
 *      exchange).
 */

// We intentionally don't ship `@types/google.maps`. The Map/Marker surface
// we use is tiny and lives behind `any` in DraggableSatelliteMap.tsx.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GoogleNamespace = any

let inflight: Promise<GoogleNamespace> | null = null

/** True once the dynamic-import loader (`google.maps.importLibrary`) is ready. */
export function isGoogleMapsLoaded(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as unknown as { google?: GoogleNamespace }).google?.maps
      ?.importLibrary === "function"
  )
}

/**
 * Google's official "inline bootstrap loader" for the dynamic library import
 * API (verbatim from the docs, only typed enough to satisfy the compiler).
 *
 * Why this and not a plain `<script src=".../maps/api/js?loading=async">`?
 * That URL only serves a ~13 KB bootstrap that defines `google.maps.Load` and
 * then fetches `main.js` asynchronously — and `importLibrary` lives in
 * `main.js`. A `<script>` `onload` therefore fires BEFORE `importLibrary`
 * exists, so any caller that resolves on `onload` and then calls
 * `importLibrary()` races and intermittently crashes with
 * "importLibrary is not a function".
 *
 * This bootstrap instead defines `google.maps.importLibrary` SYNCHRONOUSLY as
 * a lazy wrapper: it fetches the real API (with the requested libraries) on
 * the first `importLibrary()` call and resolves when ready. No race.
 * Ref: https://developers.google.com/maps/documentation/javascript/load-maps-js-api
 */
function runGoogleMapsBootstrap(config: { key: string; v?: string }): void {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const g = config as Record<string, string>
  const p = "The Google Maps JavaScript API"
  const l = "importLibrary"
  const q = "__ib__"
  const m = document
  const w = window as any
  const b: any = w.google || (w.google = {})
  const d: any = b.maps || (b.maps = {})
  const r = new Set<string>()
  const e = new URLSearchParams()
  let h: Promise<void> | undefined
  const u = () =>
    h ||
    // The `await` on `createElement` defers to a microtask so that all
    // synchronous `importLibrary()` calls in the same tick (e.g. "maps" and
    // "marker") batch their libraries into a single script load.
    // eslint-disable-next-line no-async-promise-executor
    (h = new Promise<void>(async (f, n) => {
      const a = await m.createElement("script")
      e.set("libraries", [...r] + "")
      for (const k in g)
        e.set(k.replace(/[A-Z]/g, (t) => "_" + t[0].toLowerCase()), g[k])
      e.set("callback", "google.maps." + q)
      a.src = "https://maps.googleapis.com/maps/api/js?" + e
      d[q] = f
      a.onerror = () => {
        h = undefined
        n(new Error(p + " could not load."))
      }
      a.nonce =
        (m.querySelector("script[nonce]") as HTMLScriptElement | null)?.nonce || ""
      m.head.append(a)
    }))
  if (d[l]) {
    console.warn(p + " only loads once. Ignoring:", g)
  } else {
    // After `main.js` runs it overwrites `d[l]` with the real importLibrary,
    // so the `.then` below invokes the real one (no recursion).
    d[l] = (x: string, ...n: unknown[]) => (r.add(x), u().then(() => d[l](x, ...n)))
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export function loadGoogleMaps(apiKey: string): Promise<GoogleNamespace> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps requer ambiente browser"))
  }
  const win = window as unknown as { google?: GoogleNamespace }
  if (typeof win.google?.maps?.importLibrary === "function") {
    return Promise.resolve(win.google)
  }
  if (inflight) return inflight

  inflight = new Promise<GoogleNamespace>((resolve, reject) => {
    try {
      // `v=quarterly` keeps the API on the stable channel without auto-jumping
      // to weekly updates that occasionally ship breaking changes.
      runGoogleMapsBootstrap({ key: apiKey, v: "quarterly" })
      if (typeof win.google?.maps?.importLibrary === "function") {
        resolve(win.google)
      } else {
        // Reset so a later retry can re-run the bootstrap.
        inflight = null
        reject(new Error("Google Maps: bootstrap não expôs importLibrary"))
      }
    } catch (err) {
      inflight = null
      reject(err instanceof Error ? err : new Error("Falha a inicializar Google Maps"))
    }
  })
  return inflight
}
